/* eslint-disable max-len */
/* eslint-disable jsx-a11y/anchor-is-valid */

import React, { useRef, useEffect, useState } from 'react'
import styled, { keyframes, css } from 'styled-components'
import classnames from 'classnames'
import S from './CameraScreen.css'

const aspectRatioScaleMap = {
  square : { x : 0.8, y : 0.8 },
  wide : { x : 0.9, y : 0.6 },
  tall : { x : 0.6, y : 0.9 },
}

function CameraScreen( props ) {

  const {
    onClose,
    setFormImage,
    setFormShape,
    showAspectRatioButtons = true,
    defaultAspectRatio = 'wide',
    instructionsText = null } = props

  const videoRef = useRef( null )
  const overlayRef = useRef( null )
  const photoRef = useRef( null )
  const bodyRef = useRef( null )
  const streamRef = useRef()

  const [ image, setImage ] = useState( null )

  const [ framePoints, setFramePoints ] = useState( [] )
  const [ frameSize, setFrameSize ] = useState( { w : 200, h : 332 } )
  const [ ratioMode, setRatioMode ] = useState( defaultAspectRatio ) // vals: ['wide', 'tall', 'square']
  const [ isFlashing, setIsFlashing ] = useState( false )

  const handleResize = () => {
    const parentDiv = videoRef.current?.parentNode
    const visibleRect = parentDiv?.getBoundingClientRect()
    const visibleWidth = visibleRect?.width
    const visibleHeight = visibleRect?.height
    const screenWidth = window.innerWidth

    const sx = aspectRatioScaleMap[ratioMode].x
    const sy = aspectRatioScaleMap[ratioMode].y
    // const smallSide = Math.min(videoSize?.width, videoSize?.height)
    const frameSizePadding = 30
    const sizeLimit = Math.min( visibleWidth, visibleHeight ) - frameSizePadding
    const calculatedFrameWidth = screenWidth * sx
    const calculatedFrameHeight = screenWidth * sy

    const maxFrameSide = Math.max( calculatedFrameWidth, calculatedFrameHeight )
    let downsizer = 1
    if ( maxFrameSide > sizeLimit ) {
      // need to downsize
      downsizer = sizeLimit / maxFrameSide
    }
    setFrameSize( { w : calculatedFrameWidth * downsizer, h : calculatedFrameHeight * downsizer } )
  }

  useEffect( () => {
    window.addEventListener( 'resize', handleResize )
    return () => {
      window.removeEventListener( 'resize', handleResize )
    }
  } )

  useEffect( () => {
    handleResize()
  }, [ ratioMode ] )

  useEffect( () => {
    getVideo()
  }, [ videoRef ] )

  const getVideoDimensions = () => {
    if ( !videoRef?.current ) return null
    const width = videoRef?.current?.videoWidth
    const height = videoRef?.current?.videoHeight
    return { width, height }
  }

  const getVideo = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia( {
        video : { facingMode : 'environment' }
      } )
      streamRef.current = stream
      const video = videoRef.current
      video.srcObject = stream
      await video.play()
      handleResize()
    } catch ( err ) {
      console.error( err )
    }
  }

  const takePhoto = () => {
    if ( !videoRef.current ) return

    const video = videoRef.current
    const photo = photoRef.current

    if ( !photo ) return
    if ( !video ) return

    setIsFlashing( true )
    const ctx = photo.getContext( '2d' )
    ctx.drawImage( video, 0, 0 )
    const imageDataUrl = photo.toDataURL( 'image/png' )
    setImage( imageDataUrl )
    onGetFrameSheeit()

    streamRef.current?.getTracks().forEach( ( track ) => {
      track.stop()
    } )
    video.pause()
  }

  const getRectCoords = ( { imgWidth, imgHeight, rectWidth, rectHeight } ) => {
    // console.log({ imgWidth, imgHeight, rectWidth, rectHeight })
    const xCenter = imgWidth / 2
    const yCenter = imgHeight / 2

    const leftX = xCenter - rectWidth / 2
    const topY = yCenter - rectHeight / 2

    const rightX = xCenter + rectWidth / 2
    const bottomY = yCenter + rectHeight / 2

    return [
      [ leftX, topY ],
      [ rightX, topY ],
      [ rightX, bottomY ],
      [ leftX, bottomY ]
    ]
  }

  const getVideoScale = () => {
    const videoHeight = videoRef?.current?.videoHeight
    const videoWidth = videoRef?.current?.videoWidth

    const currentWidth = videoRef?.current?.offsetWidth
    const currentHeight = videoRef?.current?.offsetHeight

    const xscale = currentWidth / videoWidth
    const yscale = currentHeight / videoHeight

    return { x : xscale, y : yscale }
  }
  const getImageLandFrameCoords = () => { // get frame coordinates in the cartesian world of the image pixels
    const scale = getVideoScale()
    const size = getVideoDimensions()

    const frameSizeImgWorld = { w : frameSize.w / scale.x, h : frameSize.h / scale.y }

    return getRectCoords( { imgWidth : size.width, imgHeight : size.height, rectWidth : frameSizeImgWorld.w, rectHeight : frameSizeImgWorld.h } )
  }

  const onGetFrameSheeit = () => {
    const polyPoints = getImageLandFrameCoords()
    setFramePoints( polyPoints )
  }

  const onClearImage = async () => {
    setImage( null )
    await getVideo()
  }

  const handleClickAspectRatio = ( e ) => {
    e.preventDefault()
    const mode = e.currentTarget.value
    setRatioMode( mode )
  }

  const flashAnimation = keyframes`
  from {
    opacity: 0.75;
  }

  to {
    opacity: 0;
  }`

  const Flash = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: #ffffff;
  opacity: 0;

  ${( { flash } ) => {
    if ( flash ) {
      return css`
        animation: ${flashAnimation} 444ms ease-out;
      `
    }
    return null
  }}`

  const handleClose = () => {
    const video = videoRef.current
    
    streamRef.current?.getTracks().forEach( ( track ) => {
      track.stop()
    } )
    video?.pause()
    onClose()
  }

  const handleContinue = ( data ) => {
    const { image : img, shape } = JSON.parse( data.target.value )
    setFormImage( img )
    setFormShape( shape )
    handleClose()
  }

  // const instructionCopy = image ? 'Confirm Defect' : 'Position the Defect in the Frame'
  const videoWidth = getVideoDimensions()?.width
  const videoHeight = getVideoDimensions()?.height
  return (
    <div className={S.fullscreenFlex}>

      {/* header */}

      <div className={S.header}>
        <div className={S.headerSideWrapper}>
          {!image && ( 
            <button type="button" onClick={handleClose} className={S.squareButton}>
              <i className="fas fa-arrow-left" />
            </button>
          )}
          {!!image && ( 
            <button type="button" onClick={onClearImage} className={S.backButton}>
              Retake
            </button>
          )}
        </div>
        {!image && showAspectRatioButtons && ( 
          <div className={S.arButtonsWrapper}>
            <button type="button" value="wide" onClick={handleClickAspectRatio} className={S.arButton} disabled={ratioMode === 'wide'}>
              <div className={S.aspectRatioIconWrapper}>
                <div className={S.arButtonWide} />
              </div>
              Wide
            </button>
            <button type="button" value="tall" onClick={handleClickAspectRatio} className={S.arButton} disabled={ratioMode === 'tall'}>
              <div className={S.aspectRatioIconWrapper}>
                <div className={S.arButtonTall} />
              </div>
              Tall
            </button>
            <button type="button" value="square" onClick={handleClickAspectRatio} className={S.arButton} disabled={ratioMode === 'square'}>
              <div className={S.aspectRatioIconWrapper}>
                <div className={S.arButtonSquare} />
              </div>
              Square
            </button>
          </div>
        )}
        <div className={S.headerSideWrapper} />
      </div>

      {/* body */}
      <div ref={bodyRef} className={S.body}>
        <div className={classnames( S.overlayWrapper, S.flashOverlayWrapper )}>
          <Flash
            flash={isFlashing ? true : undefined}
            onAnimationEnd={() => setIsFlashing( false )} />
          { instructionsText && (
            <a className={S.instructionCopy}>{instructionsText}</a>
          ) }
          {image && <button type="button" value={JSON.stringify( { image, shape : framePoints } )} onClick={handleContinue} className={S.continueButton}>Continue</button>}
        </div>
        <div className={S.overlayWrapper}>
          <div ref={overlayRef} className={S.frameBox} style={{ width : frameSize.w, height : frameSize.h }} />
        </div>

        {!image && ( 
          <>
            <div id="video-wrapper" className={S.videoWrapper}>
              <video 
                id="video"
                height="100%"
                muted
                ref={videoRef}
                autoPlay
                playsInline/>
            </div>
            <button type="button" onClick={takePhoto} className={S.shutterButton} />
          </>
        )}
        {image && (
          <div className={S.imgContainer}>
            <img src={image} alt="captured" />
          </div>
        )}
        <canvas hidden width={videoWidth} height={videoHeight} ref={photoRef} />
      </div>
      {/* end body */}
      
    </div>
  )
}

export default CameraScreen
